
















































import { Component, Prop, Watch } from "vue-property-decorator";
import { LineItem, Order } from "@/models/order/order";
import { ProductType } from "@/models/products/product";
import { priceLevelMultiplier } from "@/utility/helpers";
import QtyPicker from "@/components/elements/utility/QtyPicker.vue";
import EditMultiplier from "@/components/elements/utility/EditMultiplier.vue";
import SurchargeBanner from "@/components/footer/SurchargeBanner.vue";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderGetters } from "@/store/modules/order/types";
import { Mobile } from "@/mixins/mobile";
import { mixins } from "vue-class-component";
import { GlobalGetters } from "@/store/modules/global/types";
import { CustomUmbrellaOrder } from "@/models/configurator/custom_umbrella";
import { PriceLevel } from "@/models/netsuite/netsuite";


/** SEE PRICING DIAGRAM FOR MORE INFORMATION */
@Component({
  components: {
    QtyPicker,
    EditMultiplier,
    SurchargeBanner
  }
})
export default class AddToQuoteFooter extends mixins(Mobile) {
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetAssignedCustomer))
  customer!: string;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetMultiplier))
  multiplier!: PriceLevel | null;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrderItems))
  items!: LineItem[];
  @Prop() type!: ProductType;
  @Prop() model!: string;
  @Prop() itemPrice!: number;
  @Prop() itemDetails?: CustomUmbrellaOrder["canopy"];
  @Prop() displayName!: string;
  @Prop() productLineName!: string;
  @Prop() editMode!: boolean;
  @Prop() sku!: string;

  protected allowSubmit = true;

  protected lineItem: LineItem = {
    product_type: this.type,
    multiplier: 1,
    sku: "",
    model: "",
    product_line: "",
    qty: 1,
    unit_price: 0,
    display_name: "",
    notes: ""
  };
  protected originalItem: LineItem | null = null;

  @Watch("multiplier")
  private onStoreMultiplierChange(): void {
    this.lineItem.multiplier = this.multiplierValue;
  }

  @Watch("sku")
  private updateSKU(): void {
    this.lineItem.sku = this.sku;
  }

  created() {
    if (this.editMode) {
      this.updateOriginalItem();
    }
    this.lineItem.qty = this.originalItem?.qty ? this.originalItem.qty : 1;
    this.lineItem.multiplier = this.multiplierValue;
  }

  protected get multiplierValue(): number {
    if (!this.multiplier || this.multiplier.discountpct == null) return 1;
    return priceLevelMultiplier(this.multiplier.discountpct);
  }

  protected onMultiplierChange(level: PriceLevel | null): void {
    this.lineItem.multiplier = priceLevelMultiplier(level?.discountpct);
  }

  protected updateOriginalItem(): void {
    const index = parseInt(this.$route.query.cart as string, 10);
    this.originalItem = this.items[index];
  }

  protected submitItem(): void {
    this.lineItem.model = this.model;
    this.lineItem.display_name = this.displayName;
    this.lineItem.product_line = this.productLineName;
    this.lineItem.unit_price = this.itemPrice;
    this.lineItem.sku = this.sku;
    if (this.editMode) {
      // NetSuite SKU-parsed lines come back with an empty top-level
      // product_line (the real one lives on item_details.layout); editing
      // resolves it, so an empty original is not a genuine product-line
      // switch. Only force a resubmit when the original actually carried a
      // product_line that now differs.
      //
      // Bases are exempt: a base page is routed by model, not product line, so
      // `productLineName` here IS the model (see editItem in mixins/orders) and
      // that is what this app has always stored on a base line. A line the SKU
      // parser rebuilt instead carries the model's real catalog product line, so
      // the two are different kinds of value and never compare equal — the guard
      // would fire on every NetSuite-added base. Nothing is lost by skipping it:
      // the base page can change neither value, both being fixed by the route.
      const originalProductLine = this.originalItem?.product_line;
      const productLineChanged =
        this.type !== ProductType.Base &&
        !!originalProductLine &&
        originalProductLine !== this.lineItem.product_line;
      if (
        this.originalItem?.product_type !== this.lineItem.product_type ||
        productLineChanged
      ) {
        this.$router.replace("/resubmit");
      } else {
        this.$emit("editQuote", this.lineItem);
      }
    } else {
      this.$emit("addToQuote", this.lineItem);
    }
  }

  protected setQty(qty: number) {
    this.lineItem.qty = qty;
  }

  protected toggleSubmit(res: boolean) {
    this.allowSubmit = res;
  }

  get price(): number {
    return this.itemPrice * this.lineItem.multiplier * this.lineItem.qty;
  }

  get MSRP(): number {
    return this.itemPrice * this.lineItem.qty;
  }
}
